import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { useRootSelector } from '@/hooks/selector.hook';
import { type ReactNode, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import ProfileMenuContent from './ProfileMenuContent';
import { getUserAvatar, getUserDisplayName, getUserInitial, getUserRoleName } from '@/utils/user';
import { Link } from 'react-router-dom';
import ButtonCustom from '@/components/ButtonCustom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/hooks/auth.hook';

interface ProfileDropdownProps {
  children?: ReactNode;
}

export default function DropdownMenuDemo({ children }: ProfileDropdownProps) {
  const { isAuthenticated, user } = useRootSelector((state) => state.auth);
  const [isOpen, setIsOpen] = useState(false);
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const { logout } = useAuth();
  const displayName = getUserDisplayName(user);
  const roleName = getUserRoleName(user);
  const avatar = getUserAvatar(user);
  const initial = getUserInitial(user);

  if (!isAuthenticated) {
    return (
      <Link
        to="/login"
        className="hidden h-[4rem] items-center rounded-full bg-[#2563EB] px-[1.6rem] text-[1.4rem] font-semibold leading-[2rem] text-white transition-colors hover:bg-[#1D4ED8] lg:flex"
      >
        Login
      </Link>
    );
  }

  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="profile lg:flex hidden cursor-pointer items-center gap-2 rounded-[5rem] bg-transparent py-[0.4rem] pl-[1.2rem] pr-[0.6rem]"
          >
            <div className="flex items-center gap-[1.6rem]">
              <div className="flex flex-col gap-[0.2rem]">
                <h3 className="text-[1.4rem] font-normal leading-[2rem] text-main whitespace-nowrap text-right">
                  {displayName}
                </h3>
                <p className="text-[1.2rem] font-normal leading-[1.4rem] text-[#637381] whitespace-nowrap text-right">
                  {roleName}
                </p>
              </div>
              <Avatar className="h-[4rem] w-[4rem]">
                <AvatarImage src={avatar} alt={displayName} />
                <AvatarFallback>{initial}</AvatarFallback>
              </Avatar>
              <ChevronDown
                size={20}
                className={`ml-[-0.8rem] transition-all duration-200 ${
                  isOpen ? 'rotate-180 text-[#1C37E0]' : 'text-[#898E99]'
                }`}
              />
            </div>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="mt-[0.8rem] min-w-[18rem] rounded-[1rem] border border-[#E2E8F0] bg-[#FFFFFF] p-[1rem] shadow-[0px_4px_7.6px_0px_#0000001A] dark:border-[#22304A] dark:bg-[#091327] dark:shadow-[0px_12px_28px_0px_rgba(0,0,0,0.35)]"
          align="end"
        >
          {children || (
            <ProfileMenuContent
              onItemSelect={() => setIsOpen(false)}
              onLogoutRequest={() => {
                setIsOpen(false);
                setIsLogoutDialogOpen(true);
              }}
            />
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={isLogoutDialogOpen} onOpenChange={setIsLogoutDialogOpen}>
        <DialogContent className="w-[calc(100%-3.2rem)] max-w-[42rem] rounded-[1.2rem] border border-[#E2E8F0] bg-[#FFFFFF] p-[2rem] dark:border-[#22304A] dark:bg-[#091327]">
          <DialogHeader className="space-y-[0.8rem]">
            <DialogTitle className="text-[1.8rem] font-semibold leading-[2.4rem] text-[#212B36] dark:text-[#E9EFFD]">
              Đăng xuất khỏi hệ thống?
            </DialogTitle>
            <DialogDescription className="text-[1.4rem] leading-[2rem] text-[#637381] dark:text-[#9FB0D0]">
              Bạn sẽ cần đăng nhập lại để tiếp tục sử dụng.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="mt-[2rem] flex-row justify-end gap-[1rem]">
            <ButtonCustom
              type="button"
              onClick={() => setIsLogoutDialogOpen(false)}
              variant="unstyled"
              className="h-[4rem] rounded-[1rem] border border-[#D0D7E2] bg-transparent px-4 text-[#212B36] hover:bg-[#E9EFFD] active:bg-[#D7E3FF] dark:border-[#31415F] dark:text-[#E9EFFD] dark:hover:bg-[#1C37E04D] dark:active:bg-[#1C37E066]"
              title="Hủy"
            >
              Hủy
            </ButtonCustom>

            <ButtonCustom
              type="button"
              variant="unstyled"
              className="h-[4rem] rounded-[1rem] bg-[#2563EB] px-4 text-white hover:bg-[#6D96F2] active:hover:bg-[#1A46A7] disabled:bg-[#BBCFF9] disabled:text-white"
              onClick={() => {
                setIsLogoutDialogOpen(false);
                logout();
              }}
            >
              Xác nhận
            </ButtonCustom>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
